import './base-input.scss';

import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { BaseInputSize } from './base-input';

type BaseTextAreaProps = {
  testId?: string;
  type?: string;
  size?: BaseInputSize;
  disabled?: boolean;
  name: string;
  value?: string;
  placeholder?: string;
  autocomplete?: string | undefined;
  shiftEnter?: boolean;
  onChange?: (value: string, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: string, e: FocusEvent<HTMLElement>) => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
};

// eslint-disable-next-line react/display-name
export const BaseTextArea = forwardRef<HTMLTextAreaElement, BaseTextAreaProps>(
  (
    {
      testId,
      type = 'text',
      size = BaseInputSize.DEFAULT,
      disabled = false,
      shiftEnter = false,
      name,
      placeholder = '',
      onChange,
      onBlur,
      ...props
    }: BaseTextAreaProps,
    outerRef
  ) => {
    const ref = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(outerRef, () => ref.current!, []);

    useEffect(() => {
      if (type === 'textarea') {
        if (ref?.current) {
          ref.current.style.height = '44px';
          ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
        }
      }
    }, [type, props.value]);

    const onHandleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (type === 'textarea') {
          if (shiftEnter && e.code === 'Enter' && !e.shiftKey) {
            e.preventDefault();
          }
        }
      },
      [shiftEnter, type]
    );

    return (
      <textarea
        {...props}
        data-test-id={testId}
        id={name}
        name={name}
        disabled={disabled}
        className={`form-control${
          size !== BaseInputSize.DEFAULT ? ` form-control--${size}` : ``
        }`}
        placeholder={placeholder}
        autoComplete={props.autocomplete}
        onChange={e => onChange?.(e.currentTarget.value, e)}
        onBlur={e => onBlur?.(e.currentTarget.value, e)}
        onKeyDown={onHandleKeyDown}
        ref={ref}
      />
    );
  }
);
